import { useMemo, useState } from 'react'
import {
  Title,
  Text,
  Card,
  Table,
  TableHead,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Badge,
  Button,
  TextInput,
  Select,
  SelectItem,
  Flex,
  Switch,
  NumberInput,
  TabGroup,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from '@tremor/react'
// Note: Using native <select> for form dropdowns due to Tremor Select issues with dark mode
import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  BellIcon,
  BellSlashIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getAlertRules,
  getAlerts,
  getAlertsSummary,
  createAlertRule,
  updateAlertRule,
  deleteAlertRule,
  toggleAlertRule,
  acknowledgeAlert,
  resolveAlert,
  type AlertRule,
  type Alert,
  type CreateAlertRuleData,
} from '@/api/alerts'
import { getOperators } from '@/api/operators'
import { AuditLogPanel } from '@/components/alerts/AuditLogPanel'
import { usePermissions } from '@/hooks/usePermissions'

const METRICS = [
  { value: 'cpu', label: 'CPU (%)' },
  { value: 'ram', label: 'RAM (%)' },
  { value: 'ping', label: 'Latence (ms)' },
  { value: 'jitter', label: 'Jitter (ms)' },
  { value: 'wifi_signal', label: 'Signal WiFi (%)' },
  { value: 'agent_offline', label: 'Agent hors ligne' },
  { value: 'mic_muted', label: 'Micro muet' },
  { value: 'audio_device_mismatch', label: 'Casque USB non utilisé' },
  { value: 'audio_emcall_missing', label: 'Audio dérivé hors EMCALL (micro/sortie PC)' },
  { value: 'battery_unplugged', label: 'Batterie non branchée' },
  { value: 'battery_percent', label: 'Batterie (%)' },
]

const OPERATORS = [
  { value: '>', label: '>' },
  { value: '<', label: '<' },
  { value: '>=', label: '>=' },
  { value: '<=', label: '<=' },
  { value: '==', label: '==' },
  { value: '!=', label: '!=' },
]

const SEVERITIES = [
  { value: 'low', label: 'Faible', color: 'blue' as const },
  { value: 'medium', label: 'Moyen', color: 'yellow' as const },
  { value: 'high', label: 'Élevé', color: 'orange' as const },
  { value: 'critical', label: 'Critique', color: 'red' as const },
]

function getSeverityBadge(severity: string) {
  const config = SEVERITIES.find(s => s.value === severity) || { label: severity, color: 'gray' as const }
  return <Badge color={config.color}>{config.label}</Badge>
}

function formatDuration(seconds: number): string {
  if (seconds === 0) return 'Immédiat'
  if (seconds < 60) return `${seconds}s`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}min`
  return `${Math.floor(seconds / 3600)}h`
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function Alerts() {
  // Viewers see alerts but cannot acknowledge/resolve or manage rules.
  const { canWrite } = usePermissions()
  const queryClient = useQueryClient()
  const [showRuleModal, setShowRuleModal] = useState(false)
  const [editingRule, setEditingRule] = useState<AlertRule | null>(null)
  const [alertFilter, setAlertFilter] = useState<'all' | 'unresolved' | 'resolved'>('unresolved')

  // Form state for rule creation/editing
  const [ruleForm, setRuleForm] = useState<CreateAlertRuleData>({
    name: '',
    description: '',
    metric: 'cpu',
    operator: '>',
    threshold: 90,
    duration_seconds: 300,
    severity: 'high',
    cooldown_minutes: 15,
    notify_google_chat: true,
    notify_email: false,
    notify_pc: true,
    only_when_in_call: false,
  })

  // Queries
  const { data: rules = [], isLoading: rulesLoading } = useQuery({
    queryKey: ['alertRules'],
    queryFn: () => getAlertRules(),
  })

  const { data: alerts = [], isLoading: alertsLoading } = useQuery({
    queryKey: ['alerts', alertFilter],
    queryFn: () => getAlerts({ resolved: alertFilter === 'resolved' ? true : alertFilter === 'unresolved' ? false : undefined }),
  })

  const { data: operators = [] } = useQuery({
    queryKey: ['operators'],
    queryFn: getOperators,
  })

  // machine_id → fullname, so the alerts table shows the operator's name
  // instead of the raw "operator-XXXXXX" id.
  const operatorNameByMachineId = useMemo(
    () => Object.fromEntries(operators.map((o) => [o.machine_id, o.fullname])),
    [operators]
  )

  const { data: summary } = useQuery({
    queryKey: ['alertsSummary'],
    queryFn: getAlertsSummary,
    refetchInterval: 30000,
  })

  // Mutations
  const createMutation = useMutation({
    mutationFn: createAlertRule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alertRules'] })
      closeRuleModal()
    },
    onError: () => { alert('Erreur lors de la création de la règle') },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CreateAlertRuleData> }) => updateAlertRule(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alertRules'] })
      closeRuleModal()
    },
    onError: () => { alert('Erreur lors de la modification de la règle') },
  })

  const deleteMutation = useMutation({
    mutationFn: deleteAlertRule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alertRules'] })
    },
    onError: () => { alert('Erreur lors de la suppression de la règle') },
  })

  const toggleMutation = useMutation({
    mutationFn: toggleAlertRule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alertRules'] })
    },
    onError: () => { alert('Erreur lors du changement de statut') },
  })

  const acknowledgeMutation = useMutation({
    mutationFn: (id: string) => acknowledgeAlert(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] })
      queryClient.invalidateQueries({ queryKey: ['alertsSummary'] })
    },
    onError: () => { alert('Erreur lors de l\'acquittement') },
  })

  const resolveMutation = useMutation({
    mutationFn: (id: string) => resolveAlert(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] })
      queryClient.invalidateQueries({ queryKey: ['alertsSummary'] })
    },
    onError: () => { alert('Erreur lors de la résolution') },
  })

  const closeRuleModal = () => {
    setShowRuleModal(false)
    setEditingRule(null)
    setRuleForm({
      name: '',
      description: '',
      metric: 'cpu',
      operator: '>',
      threshold: 90,
      duration_seconds: 300,
      severity: 'high',
      cooldown_minutes: 15,
      notify_google_chat: true,
      notify_email: false,
      notify_pc: true,
      only_when_in_call: false,
    })
  }

  const openEditModal = (rule: AlertRule) => {
    setEditingRule(rule)
    setRuleForm({
      name: rule.name,
      description: rule.description || '',
      metric: rule.metric,
      operator: rule.operator,
      threshold: rule.threshold,
      duration_seconds: rule.duration_seconds,
      severity: rule.severity,
      cooldown_minutes: rule.cooldown_minutes,
      notify_google_chat: rule.notify_google_chat,
      notify_email: rule.notify_email,
      notify_pc: rule.notify_pc,
      only_when_in_call: rule.only_when_in_call,
    })
    setShowRuleModal(true)
  }

  const handleSaveRule = () => {
    if (editingRule) {
      updateMutation.mutate({ id: editingRule.id, data: ruleForm })
    } else {
      createMutation.mutate(ruleForm)
    }
  }

  const handleDeleteRule = (id: number) => {
    if (confirm('Êtes-vous sûr de vouloir supprimer cette règle ?')) {
      deleteMutation.mutate(id)
    }
  }

  return (
    <div className="p-3 sm:p-6">
      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <Title>Alertes</Title>
        <Text>Gestion des règles d'alerte et historique</Text>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
        <Card decoration="left" decorationColor="red">
          <Text>Alertes non résolues</Text>
          <div className="text-2xl font-bold">{summary?.total_unresolved || 0}</div>
        </Card>
        <Card decoration="left" decorationColor="red">
          <Text>Critiques</Text>
          <div className="text-2xl font-bold text-red-500">{summary?.by_severity?.critical || 0}</div>
        </Card>
        <Card decoration="left" decorationColor="orange">
          <Text>Élevées</Text>
          <div className="text-2xl font-bold text-orange-500">{summary?.by_severity?.high || 0}</div>
        </Card>
        <Card decoration="left" decorationColor="yellow">
          <Text>Moyennes / Faibles</Text>
          <div className="text-2xl font-bold text-yellow-500">
            {(summary?.by_severity?.medium || 0) + (summary?.by_severity?.low || 0)}
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <TabGroup>
        <div className="overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0 mb-4 sm:mb-6">
          <TabList className="!flex-nowrap min-w-max">
            {[
              <Tab key="alerts" icon={BellIcon}>Alertes actives</Tab>,
              ...(canWrite ? [<Tab key="rules" icon={ExclamationTriangleIcon}>Règles d'alerte</Tab>] : []),
              <Tab key="audit" icon={ClockIcon}>Journal d'audit</Tab>,
            ]}
          </TabList>
        </div>

        <TabPanels>
          {/* Alerts Panel */}
          <TabPanel>
            <Card>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                <Text className="font-medium">Historique des alertes</Text>
                <Select value={alertFilter} onValueChange={(v) => setAlertFilter(v as typeof alertFilter)} className="w-full sm:w-48">
                  <SelectItem value="unresolved">Non résolues</SelectItem>
                  <SelectItem value="resolved">Résolues</SelectItem>
                  <SelectItem value="all">Toutes</SelectItem>
                </Select>
              </div>

              {alertsLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
                </div>
              ) : alerts.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-4" />
                  <Text className="text-gray-500">Aucune alerte {alertFilter === 'unresolved' ? 'non résolue' : ''}</Text>
                </div>
              ) : (
                <div className="overflow-x-auto">
                <Table className="min-w-[720px]">
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>Sévérité</TableHeaderCell>
                      <TableHeaderCell>Type</TableHeaderCell>
                      <TableHeaderCell>Machine</TableHeaderCell>
                      <TableHeaderCell>Message</TableHeaderCell>
                      <TableHeaderCell>Valeur</TableHeaderCell>
                      <TableHeaderCell>Date</TableHeaderCell>
                      <TableHeaderCell>Actions</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {alerts.map((alert: Alert) => (
                      <TableRow key={alert.id}>
                        <TableCell>{getSeverityBadge(alert.severity)}</TableCell>
                        <TableCell>{alert.alert_type}</TableCell>
                        <TableCell>
                          {operatorNameByMachineId[alert.machine_id] ? (
                            <div>
                              <div className="text-sm font-medium">{operatorNameByMachineId[alert.machine_id]}</div>
                              <div className="font-mono text-xs text-gray-500">{alert.machine_id}</div>
                            </div>
                          ) : (
                            <span className="font-mono text-sm">{alert.machine_id}</span>
                          )}
                        </TableCell>
                        <TableCell className="max-w-xs truncate">{alert.message}</TableCell>
                        <TableCell>{alert.metric_value ?? '-'}</TableCell>
                        <TableCell className="text-sm">{formatDate(alert.triggered_at)}</TableCell>
                        <TableCell>
                          {!alert.resolved_at && canWrite && (
                            <Flex justifyContent="start" className="gap-2">
                              {!alert.acknowledged_at && (
                                <Button
                                  size="xs"
                                  variant="secondary"
                                  icon={CheckIcon}
                                  onClick={() => acknowledgeMutation.mutate(alert.id)}
                                  loading={acknowledgeMutation.isPending}
                                  title="Marquer comme vu/en cours de traitement"
                                >
                                  Vu
                                </Button>
                              )}
                              <Button
                                size="xs"
                                variant="secondary"
                                color="green"
                                icon={CheckCircleIcon}
                                onClick={() => resolveMutation.mutate(alert.id)}
                                loading={resolveMutation.isPending}
                              >
                                Résoudre
                              </Button>
                            </Flex>
                          )}
                          {!alert.resolved_at && !canWrite && alert.acknowledged_at && (
                            <Badge color="amber">Vu</Badge>
                          )}
                          {alert.resolved_at && (
                            <Badge color="green">Résolu {formatDate(alert.resolved_at)}</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
              )}
            </Card>
          </TabPanel>

          {/* Rules Panel — only rendered when the role can write */}
          {canWrite && (
          <TabPanel>
            <Card>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                <Text className="font-medium">Règles de surveillance ({rules.length})</Text>
                <Button icon={PlusIcon} onClick={() => setShowRuleModal(true)} className="!min-h-[40px]">
                  Nouvelle règle
                </Button>
              </div>

              {rulesLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
                </div>
              ) : rules.length === 0 ? (
                <div className="text-center py-12">
                  <BellSlashIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <Text className="text-gray-500 mb-4">Aucune règle d'alerte configurée</Text>
                  <Button icon={PlusIcon} onClick={() => setShowRuleModal(true)}>
                    Créer une règle
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                <Table className="min-w-[900px]">
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>Actif</TableHeaderCell>
                      <TableHeaderCell>Nom</TableHeaderCell>
                      <TableHeaderCell>Condition</TableHeaderCell>
                      <TableHeaderCell>Durée</TableHeaderCell>
                      <TableHeaderCell>Sévérité</TableHeaderCell>
                      <TableHeaderCell>Cooldown</TableHeaderCell>
                      <TableHeaderCell>Notifications</TableHeaderCell>
                      <TableHeaderCell>Actions</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rules.map((rule: AlertRule) => (
                      <TableRow key={rule.id} className={!rule.is_active ? 'opacity-50' : ''}>
                        <TableCell>
                          <Switch
                            checked={rule.is_active}
                            onChange={() => toggleMutation.mutate(rule.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="font-medium flex items-center gap-2">
                            {rule.name}
                            {rule.only_when_in_call && (
                              <PhoneIcon className="h-4 w-4 text-green-500" title="Uniquement en appel" />
                            )}
                          </div>
                          {rule.description && (
                            <div className="text-xs text-gray-500">{rule.description}</div>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {rule.metric} {rule.operator} {rule.threshold}
                        </TableCell>
                        <TableCell>{formatDuration(rule.duration_seconds)}</TableCell>
                        <TableCell>{getSeverityBadge(rule.severity)}</TableCell>
                        <TableCell>{rule.cooldown_minutes}min</TableCell>
                        <TableCell>
                          <Flex justifyContent="start" className="gap-1 flex-wrap">
                            {rule.notify_google_chat && <Badge color="blue" size="xs">Chat</Badge>}
                            {rule.notify_pc && <Badge color="purple" size="xs">PC</Badge>}
                            {rule.notify_email && <Badge color="gray" size="xs">Email</Badge>}
                          </Flex>
                        </TableCell>
                        <TableCell>
                          <Flex justifyContent="start" className="gap-1">
                            <Button
                              size="xs"
                              variant="light"
                              icon={PencilIcon}
                              onClick={() => openEditModal(rule)}
                              tooltip="Modifier"
                            />
                            <Button
                              size="xs"
                              variant="light"
                              color="red"
                              icon={TrashIcon}
                              onClick={() => handleDeleteRule(rule.id)}
                              loading={deleteMutation.isPending}
                              tooltip="Supprimer"
                            />
                          </Flex>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
              )}
            </Card>
          </TabPanel>
          )}

          {/* Audit Log Panel */}
          <TabPanel>
            <AuditLogPanel limit={100} />
          </TabPanel>
        </TabPanels>
      </TabGroup>

      {/* Rule Modal */}
      {showRuleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-stretch sm:items-center justify-center z-50">
          <Card className="w-full sm:max-w-2xl sm:mx-4 sm:rounded-xl rounded-none sm:max-h-[90vh] max-h-screen overflow-y-auto">
            <Flex justifyContent="between" alignItems="center" className="mb-6">
              <Title>{editingRule ? 'Modifier la règle' : 'Nouvelle règle d\'alerte'}</Title>
              <Button variant="light" icon={XMarkIcon} onClick={closeRuleModal} />
            </Flex>

            <div className="space-y-4">
              {/* Name */}
              <div>
                <Text className="mb-1">Nom de la règle *</Text>
                <TextInput
                  placeholder="CPU Critique"
                  value={ruleForm.name}
                  onChange={(e) => setRuleForm({ ...ruleForm, name: e.target.value })}
                />
              </div>

              {/* Description */}
              <div>
                <Text className="mb-1">Description</Text>
                <TextInput
                  placeholder="Alerte si le CPU dépasse 90%..."
                  value={ruleForm.description}
                  onChange={(e) => setRuleForm({ ...ruleForm, description: e.target.value })}
                />
              </div>

              {/* Condition */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Text className="mb-1">Métrique *</Text>
                  <select
                    value={ruleForm.metric}
                    onChange={(e) => setRuleForm({ ...ruleForm, metric: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {METRICS.map(m => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Text className="mb-1">Opérateur *</Text>
                  <select
                    value={ruleForm.operator}
                    onChange={(e) => setRuleForm({ ...ruleForm, operator: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {OPERATORS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Text className="mb-1">Seuil *</Text>
                  <NumberInput
                    value={ruleForm.threshold}
                    onValueChange={(v) => setRuleForm({ ...ruleForm, threshold: v ?? 0 })}
                    min={0}
                  />
                </div>
              </div>

              {/* Duration & Cooldown */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Text className="mb-1">Durée avant déclenchement (secondes)</Text>
                  <NumberInput
                    value={ruleForm.duration_seconds}
                    onValueChange={(v) => setRuleForm({ ...ruleForm, duration_seconds: v ?? 0 })}
                    min={0}
                    step={60}
                  />
                  <Text className="text-xs text-gray-500 mt-1">
                    0 = alerte immédiate, 300 = 5 minutes
                  </Text>
                </div>
                <div>
                  <Text className="mb-1">Cooldown (minutes)</Text>
                  <NumberInput
                    value={ruleForm.cooldown_minutes}
                    onValueChange={(v) => setRuleForm({ ...ruleForm, cooldown_minutes: v ?? 15 })}
                    min={1}
                  />
                  <Text className="text-xs text-gray-500 mt-1">
                    Temps minimum entre deux alertes identiques
                  </Text>
                </div>
              </div>

              {/* Severity */}
              <div>
                <Text className="mb-1">Sévérité *</Text>
                <select
                  value={ruleForm.severity}
                  onChange={(e) => setRuleForm({ ...ruleForm, severity: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {SEVERITIES.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>

              {/* Notifications */}
              <div>
                <Text className="mb-2">Canaux de notification</Text>
                <Flex justifyContent="start" className="gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Switch
                      checked={ruleForm.notify_google_chat}
                      onChange={(v) => setRuleForm({ ...ruleForm, notify_google_chat: v })}
                    />
                    <Text>Google Chat</Text>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Switch
                      checked={ruleForm.notify_pc}
                      onChange={(v) => setRuleForm({ ...ruleForm, notify_pc: v })}
                    />
                    <Text>Notification PC</Text>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Switch
                      checked={ruleForm.notify_email}
                      onChange={(v) => setRuleForm({ ...ruleForm, notify_email: v })}
                    />
                    <Text>Email (non implémenté)</Text>
                  </label>
                </Flex>
              </div>

              {/* VoIP Condition */}
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <Flex justifyContent="start" className="gap-3">
                  <PhoneIcon className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <Switch
                        checked={ruleForm.only_when_in_call}
                        onChange={(v) => setRuleForm({ ...ruleForm, only_when_in_call: v })}
                      />
                      <div>
                        <Text className="font-medium">Uniquement en appel</Text>
                        <Text className="text-xs text-gray-500 dark:text-gray-400">
                          L'alerte ne se déclenche que si l'opérateur est actuellement en appel VoIP.
                          Recommandé pour les métriques réseau (ping, jitter, signal WiFi, micro).
                        </Text>
                      </div>
                    </label>
                  </div>
                </Flex>
              </div>

              {/* Actions */}
              <Flex justifyContent="end" className="gap-2 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <Button variant="secondary" onClick={closeRuleModal}>
                  Annuler
                </Button>
                <Button
                  onClick={handleSaveRule}
                  loading={createMutation.isPending || updateMutation.isPending}
                  disabled={!ruleForm.name || !ruleForm.metric}
                >
                  {editingRule ? 'Enregistrer' : 'Créer'}
                </Button>
              </Flex>
            </div>
          </Card>
        </div>
      )}
    </div>
  )
}
